import React, { useState, useContext, useEffect, useMemo } from 'react';
import { DataContext } from '../DataContexts/DataContext';
import { FaPencilAlt, FaCheck, FaTimes } from 'react-icons/fa';
import CoverArtEditor from '../components/CoverArtEditor';
import PropertyChecks, { trackMatchesProperties, DELETED_KEY } from './BrowseProperties';
import './ArtistEdit.css';

const normalize = (value) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

// Browse middle column (DJB-93): the artist editor in the Curator look. The
// Songs and Albums tables live in collapsible sub-sections (Songs open,
// Albums collapsed by default) and the artist's songs can be narrowed with
// the same on/off property checkmarks as the left filter area.
const ArtistEdit = ({ artist, onAlbumSelect, onSongSelect, selectedSong, selectedAlbum, deleted }) => {
  const { updateArtist } = useContext(DataContext);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(artist?.name || '');
  const [songSearchQuery, setSongSearchQuery] = useState('');
  const [activeProps, setActiveProps] = useState([]);
  const [songsOpen, setSongsOpen] = useState(true);
  const [albumsOpen, setAlbumsOpen] = useState(false);

  // Opening another artist resets the column to its defaults: Songs
  // expanded, Albums collapsed, no search or property filter
  useEffect(() => {
    setSongsOpen(true);
    setAlbumsOpen(false);
    setSongSearchQuery('');
    setActiveProps([]);
    setIsEditing(false);
  }, [artist?.id]);

  // DJB-173: the column's own Deleted checkmark opens up the deleted tracks
  // (trackMatchesProperties then narrows to deleted-only), like the legacy
  // deleted prop does for the old Music Manager page
  const includeDeleted = deleted || activeProps.includes(DELETED_KEY);

  const filteredAlbums = useMemo(() => {
    if (!artist) return [];
    const query = normalize(songSearchQuery);

    return artist.albums.filter((album) => {
      const containsNonDeletedTracks = album.tracks.some((track) => !track.deleted);
      return (
        (normalize(album.name).includes(query) ||
          album.tracks.some((track) => normalize(track.title).includes(query))) &&
        (includeDeleted || containsNonDeletedTracks) // hide fully-deleted albums in live mode
      );
    });
  }, [artist, songSearchQuery, includeDeleted]);

  const filteredSongs = useMemo(() => {
    const query = normalize(songSearchQuery);
    return filteredAlbums
      .flatMap((album) =>
        album.tracks.filter(
          (track) =>
            normalize(track.title).includes(query) &&
            (includeDeleted || !track.deleted) &&
            trackMatchesProperties(track, activeProps)
        )
      )
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [filteredAlbums, songSearchQuery, includeDeleted, activeProps]);

  const totalTracks = useMemo(() => {
    if (!artist) return 0;
    return artist.albums.reduce(
      (count, album) =>
        count + album.tracks.filter((track) => deleted || !track.deleted).length,
      0
    );
  }, [artist, deleted]);

  const handleEditClick = () => {
    setIsEditing(true);
    setEditedName(artist.name);
  };

  const handleSaveArtistNameClick = async () => {
    if (editedName !== artist.name) {
      await updateArtist({ ...artist, name: editedName });
    }
    setIsEditing(false);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditedName(artist.name);
  };

  const formatDuration = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const handleAlbumClick = (album) => {
    onSongSelect(null); // Deselect song if an album is selected
    onAlbumSelect(album);
  };

  const handleSongClick = (track) => {
    onAlbumSelect(null); // Deselect album if a song is selected
    onSongSelect(track);
  };

  if (!artist) {
    return <p className="ps-placeholder">Please select an artist to view details.</p>;
  }

  return (
    <div className="artist-edit">
      <div className="artist-header">
        <CoverArtEditor resourceId={artist.id} template="artist" />
        <div className="artist-details">
          {isEditing ? (
            <div className="edit-mode">
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className="ps-input name-input"
              />
              <FaCheck onClick={handleSaveArtistNameClick} className="icon save-icon" />
              <FaTimes onClick={handleCancelClick} className="icon cancel-icon" />
            </div>
          ) : (
            <div className="display-mode">
              <h3 className="artist-name">{artist.name}</h3>
              <FaPencilAlt onClick={handleEditClick} className="icon edit-icon" />
            </div>
          )}
          <p className="artist-songs-count">{totalTracks} songs</p>
          <p className="artist-songs-count">ID: {artist.id}</p>
        </div>
      </div>

      <input
        type="text"
        placeholder="Search albums and songs..."
        value={songSearchQuery}
        onChange={(e) => setSongSearchQuery(e.target.value)}
        className="ps-input"
      />

      <PropertyChecks active={activeProps} onChange={setActiveProps} />

      <div className="artist-subsections">
        <section className={`browse-sub ${songsOpen ? 'is-open' : 'is-collapsed'}`}>
          <h4 className="browse-sub-header">
            <button
              type="button"
              className="browse-sub-toggle"
              aria-expanded={songsOpen}
              aria-controls="artist-songs-body"
              onClick={() => setSongsOpen((open) => !open)}
            >
              <span className="browse-sub-title">Songs</span>
              <span className="browse-sub-count">{filteredSongs.length}</span>
              <span className="ps-section-chevron" aria-hidden="true" />
            </button>
          </h4>
          {songsOpen && (
            <div className="browse-sub-body" id="artist-songs-body">
              {/* DJB-103: the Songs table runs tall while Albums is
                  collapsed and hands back room as soon as it opens */}
              <div
                className={`ps-table-scroll browse-sub-scroll browse-sub-scroll--songs ${
                  albumsOpen ? 'browse-sub-scroll--shared' : ''
                }`}
              >
                <table className="ps-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSongs.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="ps-empty-cell">
                          No songs match the current filters.
                        </td>
                      </tr>
                    ) : (
                      filteredSongs.map((track) => (
                        <tr
                          key={track.id}
                          className={`browse-row ${
                            selectedSong?.id === track.id ? 'is-selected' : ''
                          } ${track.deleted ? 'is-deleted' : ''}`}
                          onClick={() => handleSongClick(track)}
                        >
                          <td className="browse-row-title" title={track.title}>
                            {track.title}
                          </td>
                          <td className="browse-row-meta">{formatDuration(track.durationMs)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        <section className={`browse-sub ${albumsOpen ? 'is-open' : 'is-collapsed'}`}>
          <h4 className="browse-sub-header">
            <button
              type="button"
              className="browse-sub-toggle"
              aria-expanded={albumsOpen}
              aria-controls="artist-albums-body"
              onClick={() => setAlbumsOpen((open) => !open)}
            >
              <span className="browse-sub-title">Albums</span>
              <span className="browse-sub-count">{filteredAlbums.length}</span>
              <span className="ps-section-chevron" aria-hidden="true" />
            </button>
          </h4>
          {albumsOpen && (
            <div className="browse-sub-body" id="artist-albums-body">
              <div className="ps-table-scroll browse-sub-scroll browse-sub-scroll--albums">
                <table className="ps-table">
                  <thead>
                    <tr>
                      <th>Album</th>
                      <th>Tracks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAlbums.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="ps-empty-cell">
                          No albums match this search.
                        </td>
                      </tr>
                    ) : (
                      filteredAlbums.map((album) => {
                        const isFullyDeleted = album.tracks.every((track) => track.deleted);
                        return (
                          <tr
                            key={album.id}
                            className={`browse-row ${
                              selectedAlbum?.id === album.id ? 'is-selected' : ''
                            } ${isFullyDeleted ? 'is-deleted' : ''}`}
                            onClick={() => handleAlbumClick(album)}
                          >
                            <td>{album.name}</td>
                            <td className="browse-row-meta">{album.tracks.length} tracks</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ArtistEdit;
